import {
	type SupportTicketListItem,
	SupportTicketStatus,
	SupportTicketType,
} from "@pilatestime-admin/shared-types";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Ticket } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { fetchSupportTicketsAdminQueryOptions } from "@/api/supportTicketsAdmin";
import { DataTable } from "@/components/data-table";
import { useApiParameters } from "@/hooks/useApiParameters";

export const Route = createFileRoute("/_auth/app/_app/support-tickets/")({
	loader: async ({ context }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);

		// Preload support tickets data
		context.queryClient.ensureQueryData(
			fetchSupportTicketsAdminQueryOptions(token),
		);

		return { userId, token };
	},
	component: SupportTicketsPage,
});

function SupportTicketsPage() {
	const { t } = useTranslation();
	const { token } = Route.useLoaderData();
	const navigate = useNavigate();

	const { data: ticketsData } = useSuspenseQuery(
		fetchSupportTicketsAdminQueryOptions(token),
	);

	const columns = useMemo<ColumnDef<SupportTicketListItem>[]>(() => {
		const getStatusBadgeClass = (status: SupportTicketStatus): string => {
			switch (status) {
				case SupportTicketStatus.OPEN:
					return "badge-warning";
				case SupportTicketStatus.WAITING_FOR_USER:
					return "badge-info";
				case SupportTicketStatus.WAITING_FOR_STAFF:
					return "badge-primary";
				case SupportTicketStatus.RESOLVED:
					return "badge-success";
				case SupportTicketStatus.CLOSED:
					return "badge-ghost";
				default:
					return "badge-outline";
			}
		};

		const getTypeBadgeClass = (type: SupportTicketType): string => {
			switch (type) {
				case SupportTicketType.COMPLAINT:
					return "badge-error";
				case SupportTicketType.REQUEST:
					return "badge-info";
				case SupportTicketType.SUGGESTION:
					return "badge-success";
				default:
					return "badge-outline";
			}
		};

		return [
			{
				accessorKey: "title",
				header: t("supportTickets.table.title"),
				cell: ({ row }) => (
					<div className="max-w-xs truncate font-medium">
						{row.original.title}
					</div>
				),
			},
			{
				accessorKey: "user",
				header: t("supportTickets.table.user"),
				cell: ({ row }) => {
					const user = row.original.user;
					const fullName =
						`${user.firstName || ""} ${user.lastName || ""}`.trim();
					return (
						<div>
							<div className="font-medium">{fullName || user.email}</div>
							{fullName && (
								<div className="text-xs text-base-content/60">{user.email}</div>
							)}
						</div>
					);
				},
			},
			{
				accessorKey: "type",
				header: t("supportTickets.table.type"),
				cell: ({ row }) => (
					<span
						className={`badge badge-sm ${getTypeBadgeClass(row.original.type)}`}
					>
						{t(`supportTickets.types.${row.original.type}`)}
					</span>
				),
			},
			{
				accessorKey: "status",
				header: t("supportTickets.table.status"),
				cell: ({ row }) => (
					<span
						className={`badge badge-sm ${getStatusBadgeClass(row.original.status)}`}
					>
						{t(`supportTickets.statuses.${row.original.status}`)}
					</span>
				),
			},
			{
				accessorKey: "createdAt",
				header: t("supportTickets.table.createdAt"),
				cell: ({ row }) => {
					const date = new Date(row.original.createdAt);
					return date.toLocaleDateString();
				},
				meta: { hiddenOnMobile: true },
			},
		];
	}, [t]);

	return (
		<div className="container mx-auto p-6 space-y-6">
			{/* Page Title */}
			<div className="flex items-center justify-between">
				<h1 className="text-2xl font-bold text-base-content">
					{t("supportTickets.title")}
				</h1>
			</div>

			{/* Support Tickets Table */}
			<div className="card bg-base-100 shadow-sm">
				<div className="card-body p-0">
					<DataTable
						data={ticketsData?.tickets || []}
						columns={columns}
						isLoading={false}
						error={null}
						testId="support-tickets"
						emptyState={{
							title: t("supportTickets.empty.title"),
							description: t("supportTickets.empty.description"),
							icon: Ticket,
						}}
						onRowClick={(row) => {
							navigate({
								to: "/app/support-tickets/$ticketId",
								params: { ticketId: row.id },
							});
						}}
					/>
				</div>
			</div>
		</div>
	);
}
